import { useState, useEffect } from "react";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Mail, KeyRound, User, Loader2, MessageSquare } from "lucide-react";

export default function Login() {
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [providers, setProviders] = useState({ discord: false, google: true });

  useEffect(() => {
    api.get("/auth/providers").then(({ data }) => setProviders(data)).catch(() => {});
    const err = new URLSearchParams(window.location.search).get("discord_error");
    if (err === "notmember") {
      toast.error("Accès réservé aux membres du serveur Discord. Rejoins le serveur puis réessaie.");
      window.history.replaceState({}, "", "/");
    } else if (err === "banned") {
      toast.error("Ce compte a été banni. Contacte un administrateur.");
      window.history.replaceState({}, "", "/");
    } else if (err) {
      toast.error("La connexion Discord a échoué. Réessaie.");
      window.history.replaceState({}, "", "/");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot", { email: form.email });
        toast.success("Si un compte existe, un email de réinitialisation vient d'être envoyé.");
        setMode("login");
        return;
      }
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email: form.email, password: form.password } : form;
      const { data } = await api.post(url, body);
      setUser(data);
      toast.success(mode === "login" ? "Connecté !" : "Compte créé !");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Échec");
    } finally {
      setBusy(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const discord = () => {
    window.location.href = `${API}/auth/discord/login`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 dot-grid">
      <div className="w-full max-w-md glass rounded-2xl border border-border p-7 fade-up">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-md overflow-hidden border border-primary/40 glow-primary shrink-0">
            <img src="/ksos-logo.jpg" alt="KSOS HUB" className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="font-head font-bold text-xl tracking-wide text-glow">KSOS HUB</div>
            <div className="text-[10px] text-muted-foreground tracking-[0.2em] uppercase">Palworld Server Companion</div>
          </div>
        </div>

        {mode === "forgot" ? (
          <div className="mb-5">
            <div className="font-head font-bold text-lg">Mot de passe oublié</div>
            <p className="text-xs text-muted-foreground mt-1">Entre ton email, on t'envoie un lien de réinitialisation.</p>
          </div>
        ) : (
          <div className="flex gap-1 p-1 rounded-lg bg-black/30 border border-border mb-5">
            {["login", "register"].map((m) => (
              <button key={m} data-testid={`tab-${m}`} onClick={() => setMode(m)}
                className={`flex-1 py-2 rounded-md text-sm font-semibold transition-colors ${mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {m === "login" ? "Connexion" : "Inscription"}
              </button>
            ))}
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input data-testid="name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Pseudo" className="cfg-input pl-9" />
            </div>
          )}
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input data-testid="email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="Email" className="cfg-input pl-9" />
          </div>
          {mode !== "forgot" && (
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input data-testid="password-input" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Mot de passe" className="cfg-input pl-9" />
            </div>
          )}
          {mode === "login" && (
            <button type="button" data-testid="forgot-password-link" onClick={() => setMode("forgot")}
              className="block text-xs text-primary hover:underline ml-auto">
              Mot de passe oublié ?
            </button>
          )}
          <button data-testid="submit-auth" type="submit" disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/85 transition-colors disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {mode === "login" ? "Se connecter" : mode === "register" ? "Créer mon compte" : "Envoyer le lien"}
          </button>
          {mode === "forgot" && (
            <button type="button" data-testid="back-to-login" onClick={() => setMode("login")}
              className="block w-full text-center text-xs text-muted-foreground hover:text-foreground">
              ← Retour à la connexion
            </button>
          )}
        </form>

        {mode !== "forgot" && (
          <>
            <div className="flex items-center gap-3 my-4">
              <div className="h-px flex-1 bg-border" /><span className="text-[10px] uppercase tracking-widest text-muted-foreground">ou</span><div className="h-px flex-1 bg-border" />
            </div>

            <button data-testid="google-login" onClick={google}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-border py-2.5 text-sm font-semibold hover:bg-[hsl(var(--surface-hover))] transition-colors">
              <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="w-4 h-4" />
              Continuer avec Google
            </button>

            {providers.discord && (
              <button data-testid="discord-login" onClick={discord}
                className="mt-2.5 w-full inline-flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-semibold text-white transition-colors hover:opacity-90"
                style={{ background: "#5865F2" }}>
                <MessageSquare className="w-4 h-4" />
                Continuer avec Discord
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
